import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  ClockIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as PendingIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const MyRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const empCode = localStorage.getItem('emp_code');
      
      // Fetch all request types from approval_requests table
      const [leaveRes, attendanceRes, overtimeRes, expenseRes] = await Promise.allSettled([
        api.get(`/leave/my`),
        api.get(`/attendance/corrections/my`, { headers: { 'x-emp-code': empCode } }),
        api.get(`/overtime/my?emp_code=${empCode}`),
        api.get(`/unified_approval/admin/requests?emp_code=${empCode}&_t=${Date.now()}`),
      ]);

      const allRequests = [];

      console.log('🔍 API Responses - Expense status:', expenseRes.status);
      if (expenseRes.status === 'fulfilled') {
        const apiResponse = expenseRes.value.data || expenseRes.value;
        console.log('📦 Expense API response type:', typeof apiResponse, 'isArray:', Array.isArray(apiResponse));
        console.log('📦 Total items in response:', Array.isArray(apiResponse) ? apiResponse.length : 0);
      }

      // Process leave requests
      if (leaveRes.status === 'fulfilled' && leaveRes.value) {
        // Handle direct array response or nested data
        const leaveRequests = Array.isArray(leaveRes.value) 
          ? leaveRes.value 
          : (leaveRes.value.data ? 
              (Array.isArray(leaveRes.value.data) ? leaveRes.value.data : leaveRes.value.data.requests || []) 
            : []);
        
        console.log('📋 Leave requests received:', leaveRequests);
        
        leaveRequests.forEach(req => {
          // Map status from approval_requests
          let displayStatus = 'pending';
          if (req.status === 'HR_APPROVED' || req.status === 'APPROVED') displayStatus = 'approved';
          else if (req.status === 'MANAGER_REJECTED' || req.status === 'HR_REJECTED' || req.status === 'REJECTED') displayStatus = 'rejected';
          else if (req.status === 'PENDING_HR') displayStatus = 'pending_hr';
          else if (req.status === 'PENDING_MANAGER') displayStatus = 'pending_manager';
          
          allRequests.push({
            id: `leave_${req.id}`,
            type: 'Leave Request',
            icon: <CalendarIcon className="w-5 h-5" />,
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            title: `${req.leave_type_code || req.leave_type || 'Leave'} Request`,
            description: `${req.from_date} to ${req.to_date}`,
            status: displayStatus,
            rawStatus: req.status,
            date: req.created_at || req.applied_date || new Date().toISOString(),
            reason: req.reason || 'N/A',
            managerReason: req.manager_reason,
            adminReason: req.admin_reason
          });
        });
      }

      // Process attendance correction requests
      if (attendanceRes.status === 'fulfilled' && attendanceRes.value) {
        const attRequests = Array.isArray(attendanceRes.value)
          ? attendanceRes.value
          : (attendanceRes.value.data ? attendanceRes.value.data : []);
        
        console.log('🔧 Attendance corrections:', attRequests);
        
        attRequests.forEach(req => {
          // Check if it's an array (old format) or object (new format)
          const isArray = Array.isArray(req);
          
          let displayStatus = 'pending';
          const status = isArray ? req[11] : req.status;
          
          if (status === 'HR_APPROVED' || status === 'APPROVED') displayStatus = 'approved';
          else if (status === 'MANAGER_REJECTED' || status === 'HR_REJECTED' || status === 'REJECTED') displayStatus = 'rejected';
          else if (status === 'PENDING_HR') displayStatus = 'pending_hr';
          else if (status === 'PENDING_MANAGER') displayStatus = 'pending_manager';
          
          const correctionDate = isArray ? req[2] : (req.correction_date || req.date);
          const reason = isArray ? req[10] : req.reason;
          const requestDate = isArray ? req[12] : (req.request_date || req.created_at || req.date);
          const id = isArray ? req[0] : req.id;
          
          allRequests.push({
            id: `attendance_${id}`,
            type: 'Attendance Correction',
            icon: <ClockIcon className="w-5 h-5" />,
            iconBg: 'bg-amber-100',
            iconColor: 'text-amber-600',
            title: 'Attendance Correction',
            description: `Date: ${correctionDate}`,
            status: displayStatus,
            rawStatus: status,
            date: requestDate,
            reason: reason,
            managerReason: isArray ? req[13] : req.manager_reason,
            adminReason: isArray ? req[14] : req.admin_reason
          });
        });
      }

      // Process overtime requests
      if (overtimeRes.status === 'fulfilled' && overtimeRes.value) {
        const otRequests = Array.isArray(overtimeRes.value)
          ? overtimeRes.value
          : [];
        
        otRequests.forEach(req => {
          let displayStatus = 'pending';
          const reqStatus = req.status || req[5] || 'pending';
          if (reqStatus === 'HR_APPROVED' || reqStatus === 'APPROVED') displayStatus = 'approved';
          else if (reqStatus === 'MANAGER_REJECTED' || reqStatus === 'HR_REJECTED' || reqStatus === 'REJECTED') displayStatus = 'rejected';
          else if (reqStatus === 'PENDING_HR') displayStatus = 'pending_hr';
          else if (reqStatus === 'PENDING_MANAGER') displayStatus = 'pending_manager';
          
          allRequests.push({
            id: `overtime_${req.id || req[0]}`,
            type: 'Overtime Request',
            icon: <ClockIcon className="w-5 h-5" />,
            iconBg: 'bg-purple-100',
            iconColor: 'text-purple-600',
            title: 'Overtime Request',
            description: `${req.hours || req[3] || 0} hours on ${req.date || req[2]}`,
            status: displayStatus,
            rawStatus: reqStatus,
            date: req.date || req[2],
            reason: req.reason || req[4] || 'N/A',
            managerReason: req.manager_reason,
            adminReason: req.admin_reason
          });
        });
      }

      // Process expense reimbursement requests from approval_requests
      if (expenseRes.status === 'fulfilled' && expenseRes.value) {
        const apiResponse = expenseRes.value.data || expenseRes.value;
        const allApiRequests = Array.isArray(apiResponse) ? apiResponse : [];
        
        console.log('📦 All API requests:', allApiRequests.length);
        if (allApiRequests.length > 0) {
          console.log('📦 First request sample:', JSON.stringify(allApiRequests[0], null, 2));
        }
        
        allApiRequests.forEach((req, index) => {
          console.log(`\n🔍 Processing request ${index + 1}:`, {
            id: req.id,
            type: req.request_type,
            payload_type: typeof req.payload,
            payload_preview: req.payload ? String(req.payload).substring(0, 100) : 'null',
            status: req.status
          });
          
          let displayStatus = 'pending';
          if (req.status === 'HR_APPROVED' || req.status === 'APPROVED') displayStatus = 'approved';
          else if (req.status === 'MANAGER_REJECTED' || req.status === 'HR_REJECTED' || req.status === 'REJECTED') displayStatus = 'rejected';
          else if (req.status === 'PENDING_HR') displayStatus = 'pending_hr';
          else if (req.status === 'PENDING_MANAGER') displayStatus = 'pending_manager';
          
          if (req.request_type === 'EXPENSE_REIMBURSEMENT') {
            let expenseData = {};
            try {
              expenseData = typeof req.payload === 'string' ? JSON.parse(req.payload) : req.payload || {};
            } catch (e) {
              console.error('Failed to parse expense data:', e, req.payload);
            }
            
            allRequests.push({
              id: `expense_${req.id}`,
              type: 'Expense Reimbursement',
              icon: <CurrencyDollarIcon className="w-5 h-5" />,
              iconBg: 'bg-green-100',
              iconColor: 'text-green-600',
              title: `${expenseData.category || 'Expense'} Reimbursement`,
              description: `Amount: ₹${(expenseData.amount || 0).toLocaleString('en-IN')}`,
              status: displayStatus,
              rawStatus: req.status,
              date: req.submitted_at,
              reason: expenseData.description || 'N/A',
              managerReason: req.manager_reason,
              adminReason: req.admin_reason
            });
          } else if (req.request_type === 'ATTENDANCE_CORRECTION') {
            let dateStr = 'N/A';
            let reason = 'N/A';
            
            console.log('🔍 ATTENDANCE_CORRECTION - Raw payload:', req.payload);
            console.log('🔍 ATTENDANCE_CORRECTION - Payload type:', typeof req.payload);
            
            try {
              if (req.payload) {
                const attData = typeof req.payload === 'string' ? JSON.parse(req.payload) : req.payload;
                console.log('🔍 ATTENDANCE_CORRECTION - Parsed attData:', attData);
                
                dateStr = attData.date || attData.correction_date || 'N/A';
                reason = attData.reason || 'N/A';
                
                console.log('🔍 ATTENDANCE_CORRECTION - Extracted:', { dateStr, reason });
              } else {
                console.warn('🔍 ATTENDANCE_CORRECTION - Payload is null/undefined');
              }
            } catch (e) {
              console.error('❌ Failed to parse attendance data:', e);
              console.error('❌ Payload was:', req.payload);
            }
            
            console.log('🔍 ATTENDANCE_CORRECTION - Final values:', { dateStr, reason });
            
            allRequests.push({
              id: `attendance_approval_${req.id}`,
              type: 'Attendance Correction',
              icon: <ClockIcon className="w-5 h-5" />,
              iconBg: 'bg-amber-100',
              iconColor: 'text-amber-600',
              title: 'Attendance Correction',
              description: `Date: ${dateStr}`,
              status: displayStatus,
              rawStatus: req.status,
              date: req.submitted_at,
              reason: reason,
              managerReason: req.manager_reason,
              adminReason: req.admin_reason
            });
          }
        });
      }

      // Sort by date (newest first)
      allRequests.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setRequests(allRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, rawStatus, managerReason, adminReason) => {
    const statusLower = status?.toLowerCase() || 'pending';
    const empCode = localStorage.getItem('emp_code');
    const isManager = empCode && empCode.startsWith('MAN-');
    
    if (statusLower === 'approved') {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
          <CheckCircleIcon className="w-4 h-4" />
          <span className="text-xs font-semibold">Approved by HR</span>
        </div>
      );
    }
    
    if (statusLower === 'rejected') {
      const rejectionReason = adminReason || managerReason;
      // For managers, always show "Rejected by HR" since their requests go directly to HR
      const rejectedBy = (isManager || adminReason) ? 'HR' : 'Manager';
      return (
        <div className="group relative">
          <div className="flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-700 rounded-full border border-red-200 cursor-help">
            <XCircleIcon className="w-4 h-4" />
            <span className="text-xs font-semibold">Rejected by {rejectedBy}</span>
          </div>
          {rejectionReason && (
            <div className="absolute bottom-full mb-2 right-0 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="font-semibold mb-1">Rejection Reason:</div>
              <div className="text-slate-200">{rejectionReason}</div>
              <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900"></div>
            </div>
          )}
        </div>
      );
    }
    
    if (statusLower === 'pending_hr') {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
          <PendingIcon className="w-4 h-4" />
          <span className="text-xs font-semibold">Pending with HR</span>
        </div>
      );
    }
    
    if (statusLower === 'pending_manager') {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
          <PendingIcon className="w-4 h-4" />
          <span className="text-xs font-semibold">Pending with Manager</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200">
        <PendingIcon className="w-4 h-4" />
        <span className="text-xs font-semibold">In Progress</span>
      </div>
    );
  };

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    if (filter === 'pending') return req.status === 'pending_manager' || req.status === 'pending_hr' || req.status === 'pending';
    return req.status === filter;
  }).filter(req => {
    // Apply date range filter if set
    if (dateRange.from && dateRange.to) {
      const reqDate = new Date(req.date);
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      return reqDate >= fromDate && reqDate <= toDate;
    }
    return true;
  });

  const downloadHistory = () => {
    try {
      const emp_code = localStorage.getItem('emp_code') || 'EMPLOYEE';
      const emp_name = localStorage.getItem('user_name') || 'Employee';

      // Metadata rows
      const metadataRows = [
        { 'My Requests History': 'Employee Code', 'Value': emp_code },
        { 'My Requests History': 'Employee Name', 'Value': emp_name },
        { 'My Requests History': 'Filter Applied', 'Value': filter.charAt(0).toUpperCase() + filter.slice(1) },
        { 'My Requests History': 'Total Records', 'Value': filteredRequests.length },
        { 'My Requests History': 'Generated On', 'Value': new Date().toLocaleString() },
        { 'My Requests History': '', 'Value': '' }
      ];

      // Data rows
      const dataRows = filteredRequests.map(req => ({
        'Request Type': req.type,
        'Title': req.title,
        'Description': req.description,
        'Reason': req.reason || 'N/A',
        'Status': req.status.charAt(0).toUpperCase() + req.status.slice(1),
        'Requested Date': new Date(req.date).toLocaleDateString('en-IN')
      }));

      const allRows = [...metadataRows, ...dataRows];
      const ws = XLSX.utils.json_to_sheet(allRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'My Requests');
      XLSX.writeFile(wb, `My_Requests_History_${new Date().getTime()}.xlsx`);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#D8E3F0] p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A90E2]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#D8E3F0] p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">My Requests</h3>
          <p className="text-sm text-slate-500 mt-1">Track all your requests in one place</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistoryModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition"
          >
            <ClockIcon className="w-3.5 h-3.5" />
            View History
          </button>
          <button
            onClick={downloadHistory}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition"
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            Download
          </button>
          <DocumentTextIcon className="w-6 h-6 text-[#4A90E2]" />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-100 pb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            filter === 'all'
              ? 'bg-[#4A90E2] text-white'
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          All ({requests.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            filter === 'pending'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          Pending ({requests.filter(r => r.status === 'pending' || r.status === 'pending_manager' || r.status === 'pending_hr').length})
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            filter === 'approved'
              ? 'bg-green-500 text-white'
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          Approved ({requests.filter(r => r.status === 'approved').length})
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            filter === 'rejected'
              ? 'bg-red-500 text-white'
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          Rejected ({requests.filter(r => r.status === 'rejected').length})
        </button>
      </div>

      {/* Requests List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No requests found</p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <div
              key={request.id}
              className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-[#4A90E2]/30 hover:bg-[#EDF4FF]/30 transition group"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className={`${request.iconBg} ${request.iconColor} p-3 rounded-xl group-hover:scale-110 transition`}>
                  {request.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-bold text-slate-900">{request.title}</h4>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs text-slate-500">{request.type}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">{request.description}</p>
                  {request.reason && (
                    <p className="text-xs text-slate-500 italic line-clamp-2">Reason: {request.reason}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Requested on {request.date && !isNaN(new Date(request.date).getTime()) 
                      ? new Date(request.date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
              <div className="ml-4">
                {getStatusBadge(request.status, request.rawStatus, request.managerReason, request.adminReason)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative shadow-xl">
            <button
              onClick={() => {
                setShowHistoryModal(false);
                setDateRange({ from: '', to: '' });
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-bold text-slate-900 mb-4">View History</h3>
            <p className="text-sm text-slate-600 mb-4">Select date range to filter requests</p>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-2">From Date</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-2">To Date</label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setDateRange({ from: '', to: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 transition"
                >
                  Clear Filter
                </button>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRequests;
